"use client";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageV3Analytics } from "@/context/AnalyticsProvider/gamePageV3";
import { cn } from "@/lib/utils";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { GamePageModal } from "../GamePageModal";

const FullScreenMedia = dynamic(
  () =>
    import("../FullScreenMedia/FullScreenMedia").then((m) => m.FullScreenMedia),
  {
    ssr: false,
  },
);

interface GameplayModalProps {
  mediaURLs: string[];
  gameTitle?: string;
  gameIcon?: string;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  gameId?: string;
}

export const GameplayModal = ({
  mediaURLs,
  gameTitle,
  gameIcon,
  isModalOpen,
  setIsModalOpen,
  gameId,
}: GameplayModalProps) => {
  const { track } = useGamePageV3Analytics();
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState<number | null>(
    null,
  );
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Initialize video refs array
  useEffect(() => {
    videoRefs.current = videoRefs.current.slice(0, mediaURLs.length);
  }, [mediaURLs.length]);

  // Autoplay first video on mount
  useEffect(() => {
    if (isModalOpen && videoRefs.current.length > 0) {
      const firstVideoIndex = mediaURLs.findIndex(
        (url) => !url.endsWith(".webp"),
      );
      if (firstVideoIndex !== -1 && videoRefs.current[firstVideoIndex]) {
        videoRefs.current[firstVideoIndex]?.play();
        setCurrentPlayingIndex(firstVideoIndex);
      }
    }
  }, [isModalOpen, mediaURLs]);

  useEffect(() => {
    if (isModalOpen && gameId) {
      track({
        action: Action.VIEWED,
        extras: {
          id: "gameplay-modal",
          gameId,
          gameName: gameTitle,
        },
      });
    }
  }, [isModalOpen, gameId, gameTitle, track]);

  const handleMediaOpen = (index: number) => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "gameplay-media",
          control: "GameplayMedia",
          controlType: ControlType.IMAGE,
          gameId,
          gameName: gameTitle,
          imageIndex: index.toString(),
        },
      });
    }
    setMediaIndex(index);
    setIsMediaOpen(true);
  };

  const handleMediaHover = (index: number) => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "gameplay-media-hover",
          control: "GameplayMediaHover",
          controlType: ControlType.IMAGE,
          gameId,
          gameName: gameTitle,
          imageIndex: index.toString(),
        },
      });
    }
    setMediaIndex(index);

    // Only handle video play/pause logic for video elements
    const mediaUrl = mediaURLs[index];
    if (!mediaUrl.endsWith(".webp")) {
      // Pause currently playing video if there is one
      if (currentPlayingIndex !== null && currentPlayingIndex !== index) {
        const currentVideo = videoRefs.current[currentPlayingIndex];
        if (currentVideo) {
          currentVideo.pause();
        }
      }

      // Play the hovered video
      const hoveredVideo = videoRefs.current[index];
      if (hoveredVideo) {
        hoveredVideo.play();
        setCurrentPlayingIndex(index);
      }
    }
  };

  const handleCloseMedia = () => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "gameplay-close-fullscreen",
          control: "CloseGameplayModal",
          controlType: ControlType.BUTTON,
          gameId,
          gameName: gameTitle,
        },
      });
    }
    setIsMediaOpen(false);
  };

  return (
    <>
      <GamePageModal
        title="Gameplay Modal"
        gameTitle={gameTitle ?? "Game Title"}
        gameIcon={gameIcon}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        className="h-[95%] top-[52.5%] before:rounded-b-none [&>div]:rounded-b-none"
        gameId={gameId}
      >
        {/* Bento/Masonry Images Grid */}
        <div className="z-50 relative p-5 grid gap-5 overflow-y-auto auto-rows-[max-content] grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))] md:auto-rows-[minmax(200px,240px)] lg:auto-rows-[minmax(220px,280px)] xl:auto-rows-[minmax(200px,320px)]">
          {mediaURLs.map((mediaUrl, index) => (
            <button
              type="button"
              key={mediaUrl}
              className={cn(
                "relative overflow-hidden rounded-lg group cursor-pointer",
                index % 2 === 0 ? "md:row-span-2" : "md:row-span-1",
              )}
              onClick={() => handleMediaOpen(index)}
              onMouseEnter={() => handleMediaHover(index)}
              aria-label={`View ${gameTitle ?? "game"} gameplay ${index + 1}`}
            >
              {mediaUrl.endsWith(".webp") ? (
                <Image
                  src={mediaUrl}
                  alt={`${gameTitle ?? "Game"} Gameplay ${index + 1}`}
                  fill
                  className="object-cover !relative transition-transform duration-300 ease-in-out group-hover:scale-105 size-full"
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                />
              ) : (
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  src={mediaUrl}
                  preload="metadata"
                  playsInline
                  muted
                  loop
                  className="object-cover !relative transition-transform duration-300 ease-in-out group-hover:scale-105 size-full"
                />
              )}
            </button>
          ))}
        </div>
      </GamePageModal>

      <FullScreenMedia
        mediaURL={mediaURLs[mediaIndex]}
        gameTitle={gameTitle}
        handleCloseMedia={handleCloseMedia}
        isMediaOpen={isMediaOpen}
        gameId={gameId}
      />
    </>
  );
};
